import { ReactElement, useMemo } from 'react';
import { Select, SelectProps } from '../Form';
import { Avatar } from '../Avatar';
import { ValueLabelOption } from '../Form/Select/types';
import {
  components,
  GroupBase,
  MultiValueProps,
  OptionProps,
  Options,
  SingleValueProps,
  StylesConfig,
  ValueContainerProps,
} from 'react-select';
import { Icon, IconName } from '../Graphics';
import { AvatarSize } from '../../enums/Avatar';
import defaultStyles from '../Form/Select/styles.module.scss';
import styles from './SelectTeamMember.module.scss';
import { SelectComponents } from 'react-select/dist/declarations/src/components';
import { joinClassNames } from '../../utils';

export type UserDetails = {
  userId: number;
  firstName: string;
  lastName: string;
  profileImage?: string;
};

export type UserDetailsOption = ValueLabelOption<UserDetails>;

const Option =
  <IsMulti extends boolean, Group extends GroupBase<UserDetailsOption>>(avatarSize: AvatarSize) =>
  ({
    data,
    innerProps,
    innerRef,
    isSelected,
    ...props
  }: OptionProps<UserDetailsOption, IsMulti, Group>): ReactElement => (
    <components.Option
      {...props}
      data={data}
      isSelected={isSelected}
      className={joinClassNames(
        styles.selectTeamMemberOption,
        isSelected && styles.selectTeamMemberOptionSelected,
        props.className ?? ''
      )}
      innerProps={{
        ...innerProps,
        role: 'menuitem',
      }}
      innerRef={innerRef}
    >
      {isSelected ? (
        <div className={defaultStyles.selectedIcon}>
          <Icon name={IconName.Check} size="1em" strokeWidth={2.5} />
        </div>
      ) : (
        <div className={styles.notSelectedPlaceholder} />
      )}
      <Avatar
        userId={data.value.userId}
        firstName={data.value.firstName}
        lastName={data.value.lastName}
        profileImage={data.value.profileImage}
        size={avatarSize}
        className={styles.optionAvatar}
      />
      <span>{`${data.value.firstName} ${data.value.lastName}`}</span>
    </components.Option>
  );

const SingleValue =
  <IsMulti extends boolean, Group extends GroupBase<UserDetailsOption>>(avatarSize: AvatarSize) =>
  ({ data, innerProps }: SingleValueProps<UserDetailsOption, IsMulti, Group>) => (
    <div {...innerProps} className={styles.value}>
      <Avatar
        className={styles.avatar}
        userId={data.value.userId}
        firstName={data.value.firstName}
        lastName={data.value.lastName}
        profileImage={data.value.profileImage}
        size={avatarSize}
      />
    </div>
  );

const MultiValue =
  <IsMulti extends boolean, Group extends GroupBase<UserDetailsOption>>(avatarSize: AvatarSize) =>
  ({ data, innerProps, ...props }: MultiValueProps<UserDetailsOption, IsMulti, Group>) => (
    <div {...innerProps} onClick={props.removeProps.onClick} className={styles.value}>
      <Avatar
        className={styles.avatar}
        userId={data.value.userId}
        firstName={data.value.firstName}
        lastName={data.value.lastName}
        profileImage={data.value.profileImage}
        size={avatarSize}
      />
    </div>
  );

const ValueContainer = <IsMulti extends boolean, Group extends GroupBase<UserDetailsOption>>(
  props: ValueContainerProps<UserDetailsOption, IsMulti, Group>
) => (
  <div {...props.innerProps} className={styles.valueContainer}>
    {props.children}
  </div>
);

const createSelectTeamMemberStyles = <
  IsMulti extends boolean,
  Group extends GroupBase<UserDetailsOption> = GroupBase<UserDetailsOption>,
>(
  avatarSize: AvatarSize
): StylesConfig<UserDetailsOption, IsMulti, Group> => ({
  control: (base) => ({
    ...base,
    background: 'none',
    borderWidth: 0,
    display: 'flex',
    padding: 0,
    height: `${avatarSize ?? AvatarSize.Table}px`,
    alignItems: 'center',
    justifyItems: 'flex-start',
  }),
  indicatorSeparator: (base) => ({ ...base, display: 'none' }),
  indicatorsContainer: (base) => ({
    ...base,
    flex: '1 1 auto',
    height: `${avatarSize ?? AvatarSize.Table}px`,
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: 0,
    marginLeft: '4px',
    flex: '1 1 auto',
  }),
  valueContainer: (_, { isMulti }) => ({
    alignSelf: 'center',
    height: `${avatarSize ?? AvatarSize.Table}px`,
  }),
  clearIndicator: () => ({ display: 'none' }),
  menu: (base) => ({ ...base, minWidth: 'max-content' }),
  input: (base) => ({ ...base, maxWidth: 0, width: 0, overflow: 'hidden' }),
});

const isOptionSelected = (option: UserDetailsOption, selectValues: Options<UserDetailsOption>) =>
  selectValues.find((opt) => opt.value.userId === option.value.userId) !== undefined;

export type SelectTeamMemberProps<
  IsMulti extends boolean,
  Group extends GroupBase<UserDetailsOption> = GroupBase<UserDetailsOption>,
> = SelectProps<UserDetailsOption, IsMulti, Group>;

export const SelectTeamMember = <
  IsMulti extends boolean,
  Group extends GroupBase<UserDetailsOption> = GroupBase<UserDetailsOption>,
>({
  avatarSize,
  ...props
}: SelectTeamMemberProps<IsMulti, Group> & { avatarSize: AvatarSize }) => {
  const selectTeamMemberStyles: StylesConfig<UserDetailsOption, IsMulti, Group> = useMemo(
    () => createSelectTeamMemberStyles(avatarSize),
    [avatarSize]
  );
  const selectComponents = useMemo(
    (): Partial<SelectComponents<UserDetailsOption, IsMulti, Group>> => ({
      Option: Option(avatarSize),
      SingleValue: SingleValue(avatarSize),
      MultiValue: MultiValue(avatarSize),
      ValueContainer,
    }),
    [avatarSize]
  );
  return (
    <Select
      {...props}
      className={joinClassNames(styles.selectTeamMember, props.className ?? '')}
      hideSelectedOptions={props.hideSelectedOptions ?? false}
      components={{ ...selectComponents, ...props.components }}
      styles={{ ...selectTeamMemberStyles, ...props.styles }}
      isOptionSelected={isOptionSelected}
    />
  );
};
